import { AlertTriangle, CheckCircle2, Minus } from "lucide-react";
import type { ElementType } from "react";

export type CompatibilityLevel =
  | "perfect"
  | "compatible"
  | "neutral"
  | "conflicting";

export interface TagOptionRef {
  id: string;
  parentTagOptionIds: string[];
}

export function expandTagsWithRelations(
  tagIds: string[],
  allTagOptions: TagOptionRef[],
): Set<string> {
  const expanded = new Set(tagIds);
  const tagMap = new Map(allTagOptions.map((t) => [t.id, t]));
  for (const id of tagIds) {
    const tag = tagMap.get(id);
    if (tag) {
      for (const parentId of tag.parentTagOptionIds) {
        expanded.add(parentId);
      }
    }
    for (const opt of allTagOptions) {
      if (opt.parentTagOptionIds.includes(id)) {
        expanded.add(opt.id);
      }
    }
  }
  return expanded;
}

export function computeCompatibility(
  contentTagIds: string[],
  targetTagIds: string[],
  contentCategoryId: string | null | undefined,
  targetCategoryId: string | null | undefined,
  allTagOptions: TagOptionRef[] = [],
): CompatibilityLevel {
  if (targetTagIds.length === 0) return "neutral";

  const expandedContentTags =
    allTagOptions.length > 0
      ? expandTagsWithRelations(contentTagIds, allTagOptions)
      : new Set(contentTagIds);
  const expandedTargetTags =
    allTagOptions.length > 0
      ? expandTagsWithRelations(targetTagIds, allTagOptions)
      : new Set(targetTagIds);

  const contentMatchesTarget = targetTagIds.filter((t) =>
    expandedContentTags.has(t),
  ).length;
  const targetMatchesContent = contentTagIds.filter((t) =>
    expandedTargetTags.has(t),
  ).length;

  const overlap = Math.max(contentMatchesTarget, targetMatchesContent);
  const total = targetTagIds.length;

  if (total === 0) return "neutral";

  const ratio = overlap / total;

  const categoryMatch =
    !!contentCategoryId &&
    !!targetCategoryId &&
    contentCategoryId === targetCategoryId;

  if (ratio >= 0.5 && categoryMatch) return "perfect";
  if (ratio >= 0.3 || categoryMatch) return "compatible";
  if (overlap === 0 && !categoryMatch && contentCategoryId && targetCategoryId)
    return "conflicting";
  return "neutral";
}

export const compatConfig: Record<
  CompatibilityLevel,
  { label: string; color: string; icon: ElementType }
> = {
  perfect: {
    label: "Perfect match",
    color: "text-green-600 dark:text-green-400",
    icon: CheckCircle2,
  },
  compatible: {
    label: "Compatible",
    color: "text-blue-600 dark:text-blue-400",
    icon: CheckCircle2,
  },
  neutral: {
    label: "Neutral",
    color: "text-muted-foreground",
    icon: Minus,
  },
  conflicting: {
    label: "Mismatch",
    color: "text-amber-600 dark:text-amber-400",
    icon: AlertTriangle,
  },
};

export const compatOrder: Record<CompatibilityLevel, number> = {
  perfect: 0,
  compatible: 1,
  neutral: 2,
  conflicting: 3,
};
